/**
 * 전역 인증 상태 관리 Store (Zustand)
 * - 사용자 정보(user), 인증 토큰(token), 회사 코드(companyCode), 사번(enumber)을 전역 관리
 * - persist 미들웨어를 통해 상태를 저장합니다.
 *   - user/enumber/companyCode 등은 localStorage에 저장(브라우저 재시작 후에도 유지)
 *   - accessToken/token 은 sessionStorage에 저장(브라우저/탭 종료 시 제거)
 *
 * [로그인 인증 수정 이슈 - 2024.12]
 * - 문제: zustand persist v5의 PersistStorage 인터페이스 불일치로 인한 저장 실패
 *   - StateStorage 인터페이스로 구현했으나, v5는 PersistStorage 인터페이스 사용
 *   - setItem에 객체가 전달되는데 JSON.parse를 시도하여 "[object Object]" 에러 발생
 * - 해결: PersistStorage 인터페이스로 변경 (getItem은 객체 반환, setItem은 객체 수신)
 * - 추가 수정: useLoginMutation에서 accessToken을 user 객체에서 분리하여 localStorage 저장 방지
 */
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

const SESSION_SUFFIX = ':session';

/**
 * 인증 PersistStorage 어댑터 (zustand/persist v5)
 * - localStorage: user/enumber/companyCode 등 "표시/컨텍스트" 데이터 유지
 * - sessionStorage: accessToken/token만 유지(브라우저/탭 종료 시 자동 삭제)
 *
 * ⚠️ 중요:
 * - PersistStorage 인터페이스: getItem은 StorageValue<S> | null, setItem은 StorageValue<S> 객체를 받음
 * - value는 { state: {...}, version?: number } 형태의 객체입니다.
 */
const authStorage = {
  getItem(name) {
    try {
      const localRaw = localStorage.getItem(name);
      const sessionRaw = sessionStorage.getItem(`${name}${SESSION_SUFFIX}`);

      if (!localRaw && !sessionRaw) {
        return null;
      }

      const localObj = localRaw ? JSON.parse(localRaw) : null;
      const sessionObj = sessionRaw ? JSON.parse(sessionRaw) : null; // ✅ local(user 정보) + session(token) 병합

      return {
        state: {
          ...(localObj?.state ?? {}),
          ...(sessionObj?.state ?? {}),
        },
        version: localObj?.version ?? sessionObj?.version,
      };
    } catch (e) {
      console.error('[authStorage.getItem] failed', e);
      return null;
    }
  },

  setItem(name, value) {
    try {
      const state = value?.state ?? {};
      const version = value?.version;

      const {
        token,
        accessToken,
        ...localState // 🔑 localStorage용
      } = state; /**
       * ✅ localStorage
       * - token 절대 저장 ❌
       */

      localStorage.setItem(
        name,
        JSON.stringify({
          state: localState,
          version,
        }),
      ); /**
       * ✅ sessionStorage
       * - token / accessToken만 저장
       */

      if (token || accessToken) {
        sessionStorage.setItem(
          `${name}${SESSION_SUFFIX}`,
          JSON.stringify({
            state: { token, accessToken },
            version,
          }),
        );
      } else {
        sessionStorage.removeItem(`${name}${SESSION_SUFFIX}`);
      }
    } catch (e) {
      console.error('[authStorage.setItem] failed', e);
      localStorage.removeItem(name);
      sessionStorage.removeItem(`${name}${SESSION_SUFFIX}`);
    }
  },

  removeItem(name) {
    localStorage.removeItem(name);
    sessionStorage.removeItem(`${name}${SESSION_SUFFIX}`);
  },
};

/**
 * useAuthStore
 * - setAuth: 로그인 성공 시 사용자 정보 및 토큰 저장
 * - clearAuth: 로그아웃 시 인증 정보 초기화
 * @returns {Object} Zustand 전역 상태 및 액션 함수
 */
function createAuthStore(set) {
  return {
    user: null, // 사용자 정보 객체
    token: null, // 인증 토큰 문자열
    accessToken: null, // 액세스 토큰
    enumber: null, // 사번
    companyCode: null, // 회사 코드
    companyList: [], // 사용자 소속 회사 목록
    isBootstrapped: false, // 인증 확정 여부
    // 로그인 시 상태 업데이트

    setAuth(user, accessToken, enumber, companyCode) {
      set((prev) => ({
        user: {
          ...prev.user, // 기존 값 유지
          ...user, // 새 값 덮어쓰기
        },
        token: accessToken,
        accessToken,
        enumber,
        companyCode,
        companyList: user?.companyList ?? prev.companyList,
      }));
    },
    updateUser(partialUser) {
      set((prev) => ({
        user: {
          ...prev.user,
          ...partialUser,
        },
      }));
    }, // Access Token만 갱신 (Refresh Token으로 재발급 받은 경우)
    // - user/enumber/companyCode는 그대로 유지합니다.
    setAccessToken(accessToken) {
      set((prev) => ({
        ...prev,
        token: accessToken,
        accessToken,
      }));
    }, // 세션 토큰만 초기화 (user/enumber/companyCode는 유지)
    // - sessionStorage에 토큰이 사라졌는데 store에 token이 남아있는 경우(구버전/비정상 상태) 리다이렉트 루프 방지용

    clearToken() {
      set((prev) => ({
        ...prev,
        token: null,
        accessToken: null,
      }));
    }, // 로그아웃 시 상태 초기화

    clearAuth() {
      set({
        user: null,
        token: null,
        accessToken: null,
        enumber: null,
        companyCode: null,
        companyList: [],

        isBootstrapped: true, // 인증 확정 여부
      });
    },
    setBootstrapped() {
      set({ isBootstrapped: true });
    },
  };
}

const useAuthStore = create(
  devtools(
    persist(createAuthStore, {
      name: 'auth-storage', // localStorage key
      storage: authStorage,
      partialize(state) {
        return {
          user: state.user,
          token: state.token,
          accessToken: state.accessToken,
          enumber: state.enumber,
          companyCode: state.companyCode,
          companyList: state.companyList,
          expiresIn: state.expiresIn,
        };
      },
      version: 1, // 상태 버전 (향후 마이그레이션 대비)
    }),
    { name: 'AuthStore' }, // Redux DevTools에서 표시될 이름
  ),
);

export default useAuthStore;
